import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import Name from 'components/Form/Name';
import Country from 'components/Form/Country';
import DateRange from 'components/Form/DateRange';
import Text from 'components/Form/Text';
import { ForeignBusinessAdviceItemModel } from 'forms/sections/foreign/business/advice/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { isPVQForm } from 'helpers/form';
import NotApplicable from 'components/Form/NotApplicable';
import Branch from 'components/Form/Branch';
import PVQName from 'components/Form/PVQName';

interface Props extends Partial<ForeignBusinessAdviceItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  formType?: string
}

export default class AdviceItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignBusinessAdviceItemModel>) => {
    this.props.onUpdate({
      Description: this.props.Description,
      Name: this.props.Name,
      Organization: this.props.Organization,
      Country: this.props.Country,
      Dates: this.props.Dates,
      Compensation: this.props.Compensation,
      CountryFocused: this.props.CountryFocused,
      Role: this.props.Role, // PVQ Only
      ReceivedCompensation: this.props.ReceivedCompensation, // PVQ Only
      ...queue,
    });
  };

  updateDescription = (values: ForeignBusinessAdviceItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateName = (values: ForeignBusinessAdviceItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateOrganization = (values: ForeignBusinessAdviceItemModel['Organization']) => {
    this.update({
      Organization: values,
    });
  };

  updateCountry = (values: ForeignBusinessAdviceItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateDates = (values: ForeignBusinessAdviceItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateCompensation = (values: ForeignBusinessAdviceItemModel['Compensation']) => {
    this.update({
      Compensation: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  updateRole = (values: ForeignBusinessAdviceItemModel['Role']) => {
    this.update({
      Role: values,
    });
  };


  updateReceivedCompensation = (values: ForeignBusinessAdviceItemModel['ReceivedCompensation']) => {
    this.update({
      ReceivedCompensation: values,
      Compensation: values?.value === 'Yes' ? this.props.Compensation : {},
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className="advice-item">
        <Field
          title={i18n.t('foreign.pvq.business.advice.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            {...this.props.Description}
            name="Description"
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            className="advice-description"
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.advice.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            {...this.props.Name}
            name="Name"
            aria-label="Who did you give this advice, support, or consultation to?"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="advice-name"
            required={this.props.required}
            attribute={`${attribute}.Name`}
            showLastFirstMiddleNameOrder
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.advice.heading.role')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Role`}
        >
          <Text
            {...this.props.Role}
            name="Role"
            onUpdate={this.updateRole}
            onError={this.props.onError}
            className="advice-role"
            required={this.props.required}
            attribute={`${attribute}.Role`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.advice.heading.organization')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Organization`}
        >
          <Text
            {...this.props.Organization}
            name="Organization"
            onUpdate={this.updateOrganization}
            onError={this.props.onError}
            className="advice-organization"
            required={this.props.required}
            attribute={`${attribute}.Organization`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.advice.heading.country')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            {...this.props.Country}
            name="Country"
            excludeUnitedStates
            className="advice-country"
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Country`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.advice.heading.dates')}
          help="foreign.pvq.business.advice.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            {...this.props.Dates}
            name="Dates"
            onUpdate={this.updateDates}
            minDateEqualTo
            onError={this.props.onError}
            className="advice-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Branch
          {...this.props.ReceivedCompensation}
          name="ReceivedCompensation"
          attribute={`${attribute}.ReceivedCompensation`}
          label={i18n.t('foreign.pvq.business.advice.heading.receivedCompensation')}
          labelSize="h4"
          className="has-received-compensation"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateReceivedCompensation}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.ReceivedCompensation?.value === 'Yes' && (
        <Field
          title={i18n.t('foreign.pvq.business.advice.heading.compensation')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Compensation`}
        >
          <Textarea
            name="Compensation"
            {...this.props.Compensation}
            onUpdate={this.updateCompensation}
            onError={this.props.onError}
            className="advice-compensation"
            required={this.props.required}
            attribute={`${attribute}.Compensation`}
          />
        </Field>
        )}
      </div>
    ) : (
      <div className="advice-item">
        <Field
          title={i18n.t('foreign.business.advice.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            name="Description"
            {...this.props.Description}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            className="advice-description"
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.advice.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Name"
            {...this.props.Name}
            aria-label="Provide the name of the individual to whom advice or support was provided"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="advice-name"
            required={this.props.required}
            attribute={`${attribute}.Name`}
          />
        </Field>
        <Field
          title={i18n.t('foreign.business.advice.heading.organization')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Organization`}
        >
          <Text
            name="Organization"
            {...this.props.Organization}
            onUpdate={this.updateOrganization}
            onError={this.props.onError}
            className="advice-organization"
            required={this.props.required}
            attribute={`${attribute}.Organization`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.advice.heading.country')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Country`}
        >
          <Country
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            className="advice-country"
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Country`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.advice.heading.dates')}
          help="foreign.business.advice.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            {...this.props.Dates}
            onUpdate={this.updateDates}
            minDateEqualTo
            onError={this.props.onError}
            className="advice-dates"
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.advice.heading.compensation')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Compensation`}
        >
          <Textarea
            name="Compensation"
            {...this.props.Compensation}
            onUpdate={this.updateCompensation}
            onError={this.props.onError}
            className="advice-compensation"
            required={this.props.required}
            attribute={`${attribute}.Compensation`}
          />
        </Field>
      </div>
    );
  }
}

AdviceItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
  CountryFocused: false,
  Role: {}, // PVQ Only
  ReceivedCompensation: {}, // PVQ Only
};
